import {
  X,
  ClipboardList,
  MapPin,
  User,
  Clock,
  Package,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type {
  Requisition, RequisitionItem 
} from '../../types';
import {
  STATUS_LABELS, STATUS_COLORS 
} from '../../types';

interface RequisitionDetailModalProps {
  readonly requisition: Requisition | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onApprove?: (requisitionId: string) => void;
  readonly onReject?: (requisitionId: string) => void;
}

const APPROVAL_STEPS = [
  {
    id: 'submitted',
    label: 'Submitted',
    description: 'Request created' 
  },
  {
    id: 'pending',
    label: 'Pending Review',
    description: 'Awaiting approval' 
  },
  {
    id: 'approved',
    label: 'Approved',
    description: 'Ready for fulfillment' 
  },
  {
    id: 'fulfilled',
    label: 'Fulfilled',
    description: 'Items delivered' 
  },
];

function getStepStatus(requisitionStatus: Requisition['status'], stepId: string): 'complete' | 'current' | 'upcoming' {
  const statusOrder = ['draft', 'pending', 'approved', 'fulfilled'];
  const stepOrder = ['submitted', 'pending', 'approved', 'fulfilled'];
  
  if (requisitionStatus === 'rejected') {
    if (stepId === 'submitted') return 'complete';
    if (stepId === 'pending') return 'current';
    return 'upcoming';
  }
  
  if (requisitionStatus === 'partial') {
    if (stepId === 'fulfilled') return 'current';
    return 'complete';
  }

  const currentIndex = statusOrder.indexOf(requisitionStatus);
  const stepIndex = stepOrder.indexOf(stepId);

  if (stepIndex < currentIndex) return 'complete';
  if (stepIndex === currentIndex) return 'current';
  return 'upcoming';
}

function ApprovalTimeline({ status }: { readonly status: Requisition['status'] }) {
  return (
    <div className="relative">
      <div className="flex items-center justify-between">
        {APPROVAL_STEPS.map((step, index) => {
          const stepStatus = getStepStatus(status, step.id);
          const isLast = index === APPROVAL_STEPS.length - 1;
          const isRejectedCurrent = stepStatus === 'current' && status === 'rejected';
          const isNonRejectedCurrent = stepStatus === 'current' && status !== 'rejected';

          return (
            <div key={step.id} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                    stepStatus === 'complete' && 'border-green-500 bg-green-500 text-white',
                    isRejectedCurrent && 'border-red-500 bg-red-500 text-white',
                    isNonRejectedCurrent && 'border-primary bg-primary text-white',
                    stepStatus === 'upcoming' && 'border-gray-300 bg-white text-gray-400'
                  )}
                >
                  {stepStatus === 'complete' && <CheckCircle className="h-5 w-5" />}
                  {isRejectedCurrent && <XCircle className="h-5 w-5" />}
                  {isNonRejectedCurrent && <Clock className="h-5 w-5" />}
                  {stepStatus === 'upcoming' && <span className="text-sm font-medium">{index + 1}</span>}
                </div>
                <div className="mt-2 text-center">
                  <p className={cn(
                    'text-sm font-medium',
                    stepStatus === 'upcoming' ? 'text-gray-400' : 'text-gray-900'
                  )}>
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-500">{step.description}</p>
                </div>
              </div>
              {isLast ? null : (
                <div className="mx-2 h-0.5 flex-1 self-start mt-5">
                  <div
                    className={cn(
                      'h-full transition-colors',
                      getStepStatus(status, APPROVAL_STEPS[index + 1].id) === 'upcoming'
                        ? 'bg-gray-200'
                        : 'bg-green-500'
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ItemsTable({ items }: { readonly items: RequisitionItem[] }) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Item
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              SKU
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              Requested
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              Approved
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              Fulfilled
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {items.map((item) => {
            const isFulfilled = item.fulfilledQuantity !== undefined && 
              item.fulfilledQuantity >= (item.approvedQuantity ?? item.requestedQuantity);
            const isPartial = item.fulfilledQuantity !== undefined && 
              item.fulfilledQuantity > 0 && 
              item.fulfilledQuantity < (item.approvedQuantity ?? item.requestedQuantity);

            return (
              <tr key={item.lineId} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{item.itemName}</div>
                  {item.notes && (
                    <div className="text-xs text-gray-500">{item.notes}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{item.sku}</td>
                <td className="px-4 py-3 text-right text-sm text-gray-900">
                  {item.requestedQuantity} {item.unit}
                </td>
                <td className="px-4 py-3 text-right text-sm text-gray-900">
                  {item.approvedQuantity === undefined ? '—' : `${item.approvedQuantity} ${item.unit}`}
                </td>
                <td className="px-4 py-3 text-right text-sm text-gray-900">
                  {item.fulfilledQuantity === undefined ? '—' : `${item.fulfilledQuantity} ${item.unit}`}
                </td>
                <td className="px-4 py-3">
                  {isFulfilled && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                      <CheckCircle className="h-3 w-3" /> Complete
                    </span>
                  )}
                  {isPartial && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                      <AlertCircle className="h-3 w-3" /> Partial
                    </span>
                  )}
                  {!isFulfilled && !isPartial && item.approvedQuantity !== undefined && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                      <Clock className="h-3 w-3" /> Pending
                    </span>
                  )}
                  {item.approvedQuantity === undefined && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                      <Clock className="h-3 w-3" /> Awaiting
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function RequisitionDetailModal({
  requisition,
  isOpen,
  onClose,
  onApprove,
  onReject,
}: RequisitionDetailModalProps) {
  if (!isOpen || !requisition) return null;

  const neededByDate = new Date(requisition.neededBy);
  const createdDate = new Date(requisition.createdAt);
  const isOverdue = neededByDate < new Date() && !['fulfilled', 'rejected'].includes(requisition.status);
  const canApprove = requisition.status === 'pending';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-background p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-primary/10 p-3">
              <ClipboardList className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">{requisition.requisitionId}</h2>
                <span className={cn('rounded-full px-3 py-1 text-sm font-medium', STATUS_COLORS[requisition.status])}>
                  {STATUS_LABELS[requisition.status]}
                </span>
              </div>
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {requisition.venueName}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <User className="h-4 w-4" />
                Requested By
              </div>
              <p className="mt-1 font-medium">{requisition.requestedByName}</p>
              <p className="text-xs text-gray-500">{createdDate.toLocaleDateString()}</p>
            </div>
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Package className="h-4 w-4" />
                Total Items
              </div>
              <p className="mt-1 text-2xl font-bold">{requisition.totalItems}</p>
            </div>
            <div className={cn(
              'rounded-lg border p-4',
              isOverdue ? 'bg-red-50 border-red-200' : 'bg-gray-50'
            )}>
              <div className={cn(
                'flex items-center gap-2 text-sm',
                isOverdue ? 'text-red-600' : 'text-gray-500'
              )}>
                <Clock className="h-4 w-4" />
                Need By
              </div>
              <p className={cn('mt-1 font-medium', isOverdue && 'text-red-700')}>
                {neededByDate.toLocaleDateString()}
              </p>
              {isOverdue && <p className="text-xs text-red-600 font-medium">Overdue</p>}
            </div>
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <CheckCircle className="h-4 w-4" />
                Approved By
              </div>
              {requisition.approvedByName ? (
                <>
                  <p className="mt-1 font-medium">{requisition.approvedByName}</p>
                  <p className="text-xs text-gray-500">
                    {requisition.approvedAt && new Date(requisition.approvedAt).toLocaleDateString()}
                  </p>
                </>
              ) : (
                <p className="mt-1 text-gray-400">Pending</p>
              )}
            </div>
          </div>

          {/* Approval Flow */}
          <div className="rounded-lg border p-6">
            <h3 className="mb-4 flex items-center gap-2 font-semibold">
              <ArrowRight className="h-5 w-5" />
              Approval Flow
            </h3>
            <ApprovalTimeline status={requisition.status} />
          </div>

          {/* Notes */}
          {requisition.notes && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-2 flex items-center gap-2 font-semibold">
                <FileText className="h-5 w-5" />
                Notes
              </h3>
              <p className="text-sm text-gray-600">{requisition.notes}</p>
            </div>
          )}

          {/* Items List */}
          <div>
            <h3 className="mb-4 flex items-center gap-2 font-semibold">
              <Package className="h-5 w-5" />
              Requisition Items ({requisition.items.length})
            </h3>
            <ItemsTable items={requisition.items} />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-background p-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {canApprove && onReject && (
            <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => onReject(requisition.requisitionId)}>
              <XCircle className="mr-2 h-4 w-4" />
              Reject
            </Button>
          )}
          {canApprove && onApprove && (
            <Button onClick={() => onApprove(requisition.requisitionId)}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Approve
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
